import { useState, useEffect, useRef } from 'react';
import Calendar from './components/Calendar';
import FloatingControls from './components/FloatingControls';
import { fetchTimetable } from './services/api';
import type { TimetableEntry } from './types/timetable';
import './App.css';

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check system preference or saved preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedTeacher, setSelectedTeacher] = useState<string>('all');
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [showSunday, setShowSunday] = useState<boolean>(false);
  const [timetableData, setTimetableData] = useState<TimetableEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoaded = useRef(false);

  useEffect(() => {
    // Apply dark mode class to body
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    if (hasLoaded.current) {
      return;
    }
    
    hasLoaded.current = true;
    
    const loadTimetable = async () => {
      setLoading(true);
      const data = await fetchTimetable();
      setTimetableData(data);
      setLoading(false);
    };

    loadTimetable();
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: isDarkMode ? '#ffffff' : '#000000'
      }}>
        Yuklanmoqda...
      </div>
    );
  }

  return (
    <div className="app">
      <Calendar 
        isDarkMode={isDarkMode} 
        selectedLocation={selectedLocation}
        selectedTeacher={selectedTeacher}
        selectedRoom={selectedRoom}
        onRoomChange={setSelectedRoom}
        showSunday={showSunday}
        timetableData={timetableData}
      />
      <FloatingControls
        isDarkMode={isDarkMode}
        selectedLocation={selectedLocation}
        selectedTeacher={selectedTeacher}
        showSunday={showSunday}
        onLocationChange={(location) => {
          setSelectedLocation(location);
          setSelectedRoom('all');
        }}
        onTeacherChange={setSelectedTeacher}
        onToggleSunday={setShowSunday}
        onToggleDarkMode={toggleDarkMode}
        timetableData={timetableData}
      />
    </div>
  );
}

export default App;